import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Layout from './components/Layout';
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import Industries from './pages/Industries';
import Complytics from './pages/Complytics';
import Contact from './pages/Contact';
import Assessment from './pages/Assessment';
import Resources from './pages/Resources';
import AdminDashboard from './pages/AdminDashboard';
import AdminLogin from './pages/AdminLogin';
import AdLanding from './pages/funnel/AdLanding';
import AuditFunnel from './pages/funnel/AuditFunnel';
import ThankYou from './pages/funnel/ThankYou';
import VulnerabilityAssessment from './pages/services/VulnerabilityAssessment';
import PenetrationTesting from './pages/services/PenetrationTesting';
import CloudSecurity from './pages/services/CloudSecurity';
import Compliance from './pages/services/Compliance';
import RiskReport from './pages/resources/RiskReport';
import Pricing from './pages/Pricing';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import SecurityPolicy from './pages/SecurityPolicy';
import AccessControlPolicy from './pages/AccessControlPolicy';
import DataProtectionPolicy from './pages/DataProtectionPolicy';
import IncidentResponsePolicy from './pages/IncidentResponsePolicy';
import VulnerabilityManagementPolicy from './pages/VulnerabilityManagementPolicy';
import LoggingMonitoringPolicy from './pages/LoggingMonitoringPolicy';
import BusinessContinuityPolicy from './pages/BusinessContinuityPolicy';
import SecurityCompliance from './pages/SecurityCompliance';
import AuditReport from './pages/AuditReport';
import ConsultationGuide from './pages/ConsultationGuide';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import FunnelLayout from './components/FunnelLayout';
import NotFound from './pages/NotFound';
import { initAnalytics, trackPageView } from './lib/analytics';

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageView(pathname);
  }, [pathname]);
  
  return null;
}

export default function App() {
  useEffect(() => {
    initAnalytics();
  }, []);

  return (
    <HelmetProvider>
      <Router>
        <ErrorBoundary>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<Layout />}>
              <Route index element={<Home />} />
              <Route path="about" element={<About />} />
              <Route path="services" element={<Services />} />
              <Route path="complytics" element={<Complytics />} />
              <Route path="industries" element={<Industries />} />
              <Route path="contact" element={<Contact />} />
              <Route path="assessment" element={<Assessment />} />
              <Route path="resources" element={<Resources />} />
              {/* Service Subpages inside Layout */}
              <Route path="services/vulnerability-assessment" element={<VulnerabilityAssessment />} />
              <Route path="services/penetration-testing" element={<PenetrationTesting />} />
              <Route path="services/cloud-security" element={<CloudSecurity />} />
              <Route path="services/compliance" element={<Compliance />} />
              {/* Admin Routes */}
              <Route path="admin/login" element={<AdminLogin />} />
              <Route path="admin" element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              } />
              <Route path="admin/consultation/:id" element={
                <ProtectedRoute>
                  <ConsultationGuide />
                </ProtectedRoute>
              } />
            </Route>

            {/* Funnel Routes (Minimal Layout) */}
            <Route element={<FunnelLayout />}>
              <Route path="lp/audit" element={<AdLanding />} />
              <Route path="audit" element={<AuditFunnel />} />
              <Route path="thank-you" element={<ThankYou />} />
              <Route path="resources/risk-report" element={<RiskReport />} />
              <Route path="pricing" element={<Pricing />} />
              <Route path="privacy-policy" element={<PrivacyPolicy />} />
              <Route path="terms-of-service" element={<TermsOfService />} />
              <Route path="security-policy" element={<SecurityPolicy />} />
              <Route path="access-control-policy" element={<AccessControlPolicy />} />
              <Route path="data-protection-policy" element={<DataProtectionPolicy />} />
              <Route path="incident-response-policy" element={<IncidentResponsePolicy />} />
              <Route path="vulnerability-management-policy" element={<VulnerabilityManagementPolicy />} />
              <Route path="logging-monitoring-policy" element={<LoggingMonitoringPolicy />} />
              <Route path="business-continuity-policy" element={<BusinessContinuityPolicy />} />
              <Route path="security-compliance" element={<SecurityCompliance />} />
              <Route path="audit/:id" element={<AuditReport />} />
            </Route>

            {/* Global Catch-all */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </ErrorBoundary>
      </Router>
    </HelmetProvider>
  );
}
